import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import type { ApiResponse, RequestParams } from '@/shared/api';
import type { ApiResponseList } from '@/shared/api/types';
import { CATEGORY_KEYS } from '../lib';
import { CategoryApi } from '../api';
import type {
  CategoryBase,
  CategoryById,
  CategoryCreatePayload,
  CategoryUpdatePayload,
  CategoryItem,
  CategoryItemListParams,
} from './types';

export const useCategoryList = (params?: RequestParams) => {
  return useQuery<ApiResponseList<CategoryBase>>({
    queryKey: CATEGORY_KEYS.list(params),
    queryFn: () => CategoryApi.getList(params),
    placeholderData: keepPreviousData,
  });
};

export const useCategoryById = (id: number) => {
  return useQuery<ApiResponse<CategoryById>>({
    queryKey: CATEGORY_KEYS.detail(id),
    queryFn: () => CategoryApi.getById(id),
    enabled: !!id,
  });
};

export const useCategoryItemList = (params?: CategoryItemListParams) => {
  const hasNameFilter = Boolean(params?.name && params.name.trim().length >= 1);
  const hasIdsFilter = Boolean(params?.ids && params.ids.length > 0);

  return useQuery<ApiResponse<CategoryItem[]>, Error, CategoryItem[] | undefined>({
    queryKey: CATEGORY_KEYS.itemList(params),
    queryFn: () => CategoryApi.getItemList(params),
    enabled: hasNameFilter || hasIdsFilter,
    placeholderData: keepPreviousData,
    select: (data) => data.result ?? [],
  });
};

export const useCreateCategory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CategoryCreatePayload) => CategoryApi.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CATEGORY_KEYS.all });
    },
  });
};

export const useUpdateCategory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CategoryUpdatePayload) => CategoryApi.update(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CATEGORY_KEYS.all });
    },
  });
};

export const useDeleteCategory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => CategoryApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: CATEGORY_KEYS.all });
    },
  });
};
